// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Box, Container, Stack, Tabs, Text } from '@mantine/core';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { AddEditCustomPropertyPopover } from 'components/Mantine/CustomProperties/AddEditCustomPropertyPopover';
import { CustomPropertiesContents } from 'components/Mantine/CustomProperties/CustomPropertiesContents';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { WORK_ORDER_LABEL_TYPE_ID } from 'components/Mantine/Labels/constants';
import { CustomStatusesAccordion } from 'components/Mantine/StatusEditor/CustomStatusesAccordion';
import { CreateLabelPopover } from 'modules/Admin/CompanyProperties/Labels/CreateLabelPopover';
import { LabelsPanelContents } from 'modules/Admin/CompanyProperties/Labels/LabelsPanelContents';

type CompanyPropertiesSection = {
  title: string;
  tooltip: string;
  Action?: () => ReactNode;
  Panel: () => ReactNode;
};

type PropertyTab = {
  tabLabel: string;
  options: CompanyPropertiesSection[];
};

const propertiesTabs = [
  {
    tabLabel: 'Work Order',
    options: [
      {
        title: 'Custom Statuses',
        tooltip: 'Create custom statuses for each work order state.',
        Panel: () => <CustomStatusesAccordion customStatusTypeName="Work Order" />,
      },
      {
        title: 'Labels',
        tooltip: 'Create custom labels for work orders.',
        Action: () => (
          <CreateLabelPopover labelTypeId={WORK_ORDER_LABEL_TYPE_ID} buttonProps={{ variant: 'light', size: 'xs' }} />
        ),
        Panel: () => <LabelsPanelContents labelTypeId={WORK_ORDER_LABEL_TYPE_ID} />,
      },
      {
        title: 'Custom Properties',
        tooltip: 'Create additional properties for work orders.',
        Action: () => (
          <AddEditCustomPropertyPopover
            customPropertyTypeName="Work Order"
            buttonLabel="New property"
            buttonSize="xs"
            withButtonIcon={false}
          />
        ),
        Panel: () => <CustomPropertiesContents customPropertyTypeName="Work Order" />,
      },
    ],
  },
  /** TODO
   * {tabLabel: 'Work Item', options: []},
   * {tabLabel: 'Work cell setup', options: []},
   */
  {
    tabLabel: 'Shipping',
    options: [
      {
        title: 'Custom Statuses',
        tooltip: 'Create custom statuses for each shipment state.',
        Panel: () => <CustomStatusesAccordion customStatusTypeName="Shipment" />,
      },
    ],
  },
  {
    tabLabel: 'Tasks',
    options: [
      {
        title: 'Custom Properties',
        tooltip: 'Create additional properties for tasks.',
        Action: () => (
          <AddEditCustomPropertyPopover customPropertyTypeName="Task" buttonLabel="New property" buttonSize="xs" />
        ),
        Panel: () => <CustomPropertiesContents customPropertyTypeName="Task" />,
      },
    ],
  },
  {
    tabLabel: 'Fabrication Items',
    options: [
      {
        title: 'Custom Properties',
        tooltip: 'Create additional properties for fabrication items.',
        Action: () => (
          <AddEditCustomPropertyPopover
            customPropertyTypeName="Fabrication Item"
            buttonLabel="New property"
            buttonSize="xs"
          />
        ),
        Panel: () => <CustomPropertiesContents customPropertyTypeName="Fabrication Item" />,
      },
    ],
  },
  /** TODO
   * {tabLabel: 'Cost Codes', options: []},
   * {tabLabel: 'Catalog', options: []},
   */
] as const satisfies PropertyTab[];

export const CompanyPropertiesPage = () => (
  <Box style={{ overflow: 'hidden auto' }}>
    <Container size="md">
      <BasePageHeader
        title="Shop Properties"
        bottomLeftComponent={
          <Text size="sm" c="dimmed">
            Manage all company level properties in one place.
          </Text>
        }
      />
      <Tabs defaultValue={propertiesTabs[0].tabLabel}>
        <Tabs.List mt="xl" mb="sm">
          {propertiesTabs.map(({ tabLabel }) => (
            <Tabs.Tab value={tabLabel} key={tabLabel}>
              {tabLabel}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {propertiesTabs.map(({ tabLabel, options }) => (
          <Tabs.Panel key={tabLabel} value={tabLabel}>
            <Stack gap="lg">
              {options.map(({ title, tooltip, Panel, ...Rest }) => (
                <DetailSection
                  key={title}
                  title={title}
                  tooltip={tooltip}
                  rightSection={'Action' in Rest && <Rest.Action />}
                  large
                >
                  <Panel />
                </DetailSection>
              ))}
            </Stack>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Container>
  </Box>
);
